import React, { useMemo } from 'react';
import { VStack, Box, Text, useColorModeValue } from '@chakra-ui/react';
import NodeCard from './NodeCard';
import AddNodeButton from './AddNodeButton';
import BranchContainer from './BranchContainer';
import { useCampaignContext } from './CampaignContext';

function Connector({ color }) {
  const defaultColor = useColorModeValue('gray.300', 'gray.600');
  return <Box w="2px" h="16px" bg={color || defaultColor} mx="auto" />;
}

/**
 * Collects all node IDs that belong to false-branch chains so they
 * can be excluded from the main linear rendering.
 */
function collectAllFalseBranchIds(nodes) {
  const nodeMap = {};
  nodes.forEach(n => { nodeMap[n.id] = n; });
  const ids = new Set();

  nodes.forEach(n => {
    if ((n.subType === 'IF_ELSE' || n.subType === 'AB_SPLIT' || n.subType === 'WAIT_FOR') && n.falseNodeId) {
      let currentId = n.falseNodeId;
      const visited = new Set();
      while (currentId && nodeMap[currentId] && !visited.has(currentId)) {
        visited.add(currentId);
        ids.add(currentId);
        currentId = nodeMap[currentId].nextNodeId;
      }
    }
  });

  return ids;
}

export default function NodeList({ onNodeClick }) {
  const { nodes, readOnly } = useCampaignContext();

  const falseBranchIds = useMemo(() => collectAllFalseBranchIds(nodes), [nodes]);

  const mainTrunkNodes = useMemo(
    () => nodes.filter(n => !falseBranchIds.has(n.id)),
    [nodes, falseBranchIds]
  );

  return (
    <VStack spacing={0} align="center" w="100%">
      {!readOnly && <AddNodeButton afterIndex={-1} />}

      {mainTrunkNodes.map((node, idx) => {
        const isBranching = node.subType === 'IF_ELSE' || node.subType === 'AB_SPLIT' || node.subType === 'WAIT_FOR';

        return (
          <React.Fragment key={node.id}>
            <Connector />
            <NodeCard node={node} index={nodes.indexOf(node)} onClick={onNodeClick} />

            {isBranching && (
              <BranchContainer ifElseNode={node} onNodeClick={onNodeClick} />
            )}

            <Connector />
            {!readOnly && <AddNodeButton afterIndex={nodes.indexOf(node)} />}
          </React.Fragment>
        );
      })}

      {mainTrunkNodes.length === 0 && !readOnly && (
        <Text textAlign="center" color="gray.500" mt={4} fontSize="sm">
          Click the + button above to add your first automation step.
        </Text>
      )}
    </VStack>
  );
}
